import { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "../../components/tailwind/button";
import { Card } from "../../components/tailwind/card";
import { Badge } from "../../components/tailwind/badge";
import {
    CheckCircle2,
    Loader2,
    PhoneCall,
    Facebook,
    Trash2,
    RefreshCw,
    MoreVertical,
    ShieldCheck,
    AlertCircle,
    Settings
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/tailwind/dropdown-menu";
import { useWhatsAppSDK } from "../../hooks/useWhatsAppSDK";
import { hasAccessFeature, hasActivePlan } from "../../utils/capabilitiesHelper";
import WhatsAppConnectionNotice from "../../components/whatsapp/WhatsAppConnectionNotice";
import SubscriptionNotice from "../../components/whatsapp/SubscriptionNotice";
import ManageProfileDialog from "../../components/whatsapp/ManageProfileDialog";
import { usePageTitle } from "../../hooks/usePageTitle";
import { loadPhoneProfileRequest } from "../../store/whatsappProfileSlice";
import { whatsappPageShell, waShell } from "./utils/whatsappPageShell";
import { WhatsAppPageIntro } from "./components/WhatsAppPageIntro";
import { WaCardBone, WaBone } from "./components/skeletons/WhatsAppPageSkeletons";

export default function ManageNumbers() {
    const dispatch = useDispatch();
    usePageTitle('Manage Numbers');
    const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
    const [selectedPhoneForProfile, setSelectedPhoneForProfile] = useState(null);
    
    const phones = useSelector(state => state.whatsapp.phones);
    const wabaLoading = useSelector(state => state.whatsapp.wabaLoading);
    const wabaError = useSelector(state => state.whatsapp.wabaError);
    const wabaAccounts = useSelector((state) => state.whatsapp.wabaAccounts) || [];
    const capabilities = useSelector((state) => state.capabilities.capabilities);
    const companyInfo = useSelector(state => state.companyInfo?.info || {});
    const companyId = useSelector(state => state.auth?.user?.companyId || companyInfo?.companyId || companyInfo?.id);
    const hasWabaId = Array.isArray(wabaAccounts) && wabaAccounts.length > 0 && wabaAccounts[0]?.wabaId;
    const hasActivePlanValue = hasActivePlan(capabilities);
    const hasPhoneManageAccess = hasAccessFeature(capabilities, 'whatsapp.phonenumber.manage');
    const enableSubscriptionLock = process.env.REACT_APP_ENABLE_SUBSCRIPTION_LOCK === 'true';
    const shouldLockPage = enableSubscriptionLock && (!hasActivePlanValue || !hasPhoneManageAccess);
    
    useEffect(() => {
        dispatch({ type: 'FETCH_WABA_IDS' });
        dispatch({ type: 'FETCH_PHONES' });
    }, [dispatch]);

    const onSignupSuccess = useCallback(() => {
        dispatch({ type: 'FETCH_PHONES' });
    }, [dispatch]);

    const { sdkLoaded, isConnecting, handleConnect } = useWhatsAppSDK(onSignupSuccess);

    const handleRefreshNumbers = () => {
        dispatch({ type: 'FETCH_PHONES', payload: { forceRefresh: true } });
    };

    const handleManageProfile = (phone) => {
        setSelectedPhoneForProfile(phone);
        setIsProfileDialogOpen(true);
        
        if (companyId && phone.wabaPhoneId) {
            dispatch(loadPhoneProfileRequest({
                companyId,
                phoneNumberId: phone.wabaPhoneId
            }));
        }
    };

    const handleCloseProfileDialog = () => {
        setIsProfileDialogOpen(false);
        setSelectedPhoneForProfile(null);
    };

    const handleSaveProfile = async (profileData) => {
        // Profile update is handled by Redux saga
        // Just close the dialog - success/error will be handled by the saga
        handleCloseProfileDialog();
    };

    const getQualityDisplay = (qualityRating) => {
        const qualityMap = {
            'GREEN': 'High',
            'YELLOW': 'Medium',
            'RED': 'Low',
            'UNKNOWN': 'Pending'
        };
        return qualityMap[qualityRating] || 'Pending';
    };

    const getTierDisplay = (currentLimit) => {
        const tierMap = {
            'TIER_50': 'Tier 1 (50/day)',
            'TIER_250': 'Tier 1 (250/day)',
            'TIER_1K': 'Tier 1 (1K/day)',
            'TIER_10K': 'Tier 2 (10K/day)',
            'TIER_100K': 'Tier 3 (100K/day)',
            'UNLIMITED': 'Unlimited'
        };
        return tierMap[currentLimit] || currentLimit;
    };

    return (
        <div className={waShell(whatsappPageShell.fluid, "animate-fade-in")}>
            {!hasWabaId && <WhatsAppConnectionNotice />}
            {shouldLockPage && <SubscriptionNotice message={!hasPhoneManageAccess ? "You don't have access to manage phone numbers. Upgrade your plan to manage phone numbers." : "Your plan has expired or you don't have an active plan. Upgrade to manage phone numbers."} />}
            <div className={`space-y-8 ${(!hasWabaId || shouldLockPage) ? 'pointer-events-none opacity-50' : ''}`}>

            <WhatsAppPageIntro
                description="Manage connected phone numbers via Meta Business Manager."
                actions={
                    <Button 
                        className="bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] hover:cursor-pointer text-white font-semibold gap-2"
                        onClick={handleConnect}
                        disabled={!sdkLoaded || isConnecting}
                    >
                        {isConnecting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Facebook className="h-4 w-4" />
                        )}
                        {isConnecting ? "In Progress..." : "Add Phone Number through Meta"}
                    </Button>
                }
            />

            {/* Info Banner */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
                <ShieldCheck className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                    <h4 className="font-semibold text-blue-900 dark:text-blue-200 text-sm">Official Meta Verification Required</h4>
                    <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                        Per WhatsApp policy, each phone number must be verified via OTP (SMS/Voice) to ensure you have ownership.
                        Once verified, the display name must be approved by Meta before you can send business-initiated messages.
                    </p>
                </div>
            </div>

            {/* Quality Rating Info Banner */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                    <h4 className="font-semibold text-amber-900 dark:text-amber-200 text-sm">Quality Rating Information</h4>
                    <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                        Quality rating appears after you start sending messages. It reflects how recipients engage with your messages and helps maintain good standing with WhatsApp.
                    </p>
                </div>
            </div>

            {/* Numbers List */}
            <div className="grid gap-4">
                {wabaLoading ? (
                    <div className="space-y-4" aria-busy="true" aria-label="Loading phone numbers">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <WaCardBone key={i} className="flex items-center gap-4">
                                <WaBone className="h-12 w-12 rounded-full shrink-0" />
                                <div className="flex-1 space-y-2">
                                    <WaBone className="h-4 w-40" />
                                    <WaBone className="h-3 w-56 max-w-full" />
                                </div>
                                <WaBone className="h-8 w-20 rounded-lg shrink-0" />
                            </WaCardBone>
                        ))}
                    </div>
                ) : wabaError ? (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
                        <AlertCircle className="h-8 w-8 text-red-500 dark:text-red-400 mx-auto mb-2" />
                        <p className="text-red-700 dark:text-red-300">{wabaError}</p>
                        <Button 
                            variant="outline" 
                            className="mt-4"
                            onClick={handleRefreshNumbers}
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Retry
                        </Button>
                    </div>
                ) : phones.length === 0 ? (
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-12 text-center">
                        <PhoneCall className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No Phone Numbers Yet</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">Add your first WhatsApp Business phone number to get started.</p>
                    </div>
                ) : (
                    phones.map((num) => (
                        <Card key={num.id} className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:shadow-md dark:hover:shadow-gray-900/50 transition-shadow">
                            <div className="flex items-start gap-4">
                                <div className="h-12 w-12 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <PhoneCall className="h-6 w-6 text-gray-500 dark:text-gray-400" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{num.displayPhoneNumber}</h3>
                                        <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 gap-1">
                                            <CheckCircle2 className="h-3 w-3" /> Verified
                                        </Badge>
                                    </div>
                                    <p className="text-gray-500 dark:text-gray-400 text-sm">{num.displayName}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-8 text-sm">
                                <div>
                                    <p className="text-gray-500 dark:text-gray-400 mb-1">Quality Rating</p>
                                    <div className="flex items-center gap-2">
                                        <div className={`h-2.5 w-2.5 rounded-full ${
                                            num.qualityRating === 'GREEN' ? 'bg-green-500' : 
                                            num.qualityRating === 'YELLOW' ? 'bg-yellow-500' : 
                                            num.qualityRating === 'RED' ? 'bg-red-500' : 
                                            'bg-gray-400'
                                        }`} />
                                        <span className="font-medium text-gray-900 dark:text-gray-100">{getQualityDisplay(num.qualityRating)}</span>
                                    </div>
                                </div>
                                <div className="hidden sm:block h-8 w-px bg-gray-200 dark:bg-gray-700" />
                                <div>
                                    <p className="text-gray-500 dark:text-gray-400 mb-1">Messaging Tier</p>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">{getTierDisplay(num.currentLimit)}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 self-end md:self-center">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                            <MoreVertical className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => handleManageProfile(num)}>
                                            <Settings className="h-4 w-4 mr-2" /> Profile Settings
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={handleRefreshNumbers}>
                                            <RefreshCw className="h-4 w-4 mr-2" /> Sync Status
                                        </DropdownMenuItem>
                                        <DropdownMenuItem disabled className="text-red-600 focus:text-red-600">
                                            <Trash2 className="h-4 w-4 mr-2" /> Delete Number
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </Card>
                    ))
                )}
            </div>

            {/* Manage Profile Dialog */}
            {selectedPhoneForProfile && (
                <ManageProfileDialog
                    isOpen={isProfileDialogOpen}
                    onClose={handleCloseProfileDialog}
                    phoneNumber={selectedPhoneForProfile.displayPhoneNumber}
                    phoneNumberId={selectedPhoneForProfile.wabaPhoneId}
                    onSave={handleSaveProfile}
                    initialData={{
                        displayName: selectedPhoneForProfile.displayName,
                        displayNamePending: selectedPhoneForProfile.displayNamePending,
                        category: selectedPhoneForProfile.category || "PROF_SERVICES",

                        description: selectedPhoneForProfile.description || "",
                        address: selectedPhoneForProfile.address || "",
                        email: selectedPhoneForProfile.email || "",
                        website1: selectedPhoneForProfile.website1 || "",
                        website2: selectedPhoneForProfile.website2 || "",
                        profileImage: selectedPhoneForProfile.profileImage || null,
                    }}
                />
            )}
            </div>
        </div>
    );
}
